/**
 * Every number box in the app selects its value when it gets focus (click or
 * Tab), so a new number can simply be typed over the old one instead of
 * fighting the cursor around a "1" or a "250".
 *
 * Installed once for the whole document rather than on each input. A plain
 * click places the caret on mouseup and drops the selection, so the value is
 * selected again just after the mouseup that comes with a focusing click;
 * later clicks inside an already focused box move the caret as usual.
 *
 * The mouseup itself must NOT be cancelled: when the focusing click lands on
 * the box's up/down arrows, the browser keeps stepping the value until it sees
 * the button come up, so a cancelled mouseup made one click count on and on.
 */
export const installSelectNumberOnFocus = () => {
  let justFocused: HTMLInputElement | null = null;

  const isNumberBox = (el: EventTarget | null): el is HTMLInputElement =>
    el instanceof HTMLInputElement && el.type === "number" && !el.readOnly && !el.disabled;

  document.addEventListener("focusin", (e) => {
    if (!isNumberBox(e.target)) return;
    justFocused = e.target;
    e.target.select();
  });

  document.addEventListener(
    "mouseup",
    (e) => {
      const box = justFocused;
      justFocused = null;
      if (!box || e.target !== box) return;
      // After the browser has placed the caret for this mouseup.
      setTimeout(() => {
        if (document.activeElement === box) box.select();
      }, 0);
    },
    true
  );
};
